import React, { ReactNode, useState } from "react"
import { css, Theme, useTheme } from "@emotion/react"
import { Button, ButtonEcomm, Icon } from "@ncs/web-legos/components"
import {
	RemoveMethodModal,
	VerifyBankAccountModal,
} from "~/views/account/components/billing-tab/components"
import { StripeBankAccount } from "@ncs/ncs-api/portal-apps"
import { VerificationSuccessful } from "./VerificationSuccessful"

interface ListOfBankAccountsProp {
	bankAccounts: StripeBankAccount[]
	addNewBankButton: ReactNode
	accountToVerify: boolean
	onSelectAccount?: (account: StripeBankAccount) => void
	defaultBankAccountId: string | undefined
}

export const ListofBankAccounts: React.FC<ListOfBankAccountsProp> = ({
	bankAccounts = [],
	addNewBankButton,
	onSelectAccount,
	defaultBankAccountId,
}) => {
	const [bankAccountToVerify, setBankAccountToVerify] = useState<StripeBankAccount | null>(null)
	const [bankAccountToRemove, setBankAccountToRemove] = useState<StripeBankAccount | null>(null)
	const [verificationSuccessful, setVerificationSuccessful] = useState(false)
	const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
		defaultBankAccountId ?? null
	)
	const theme = useTheme()

	const setSelectedAccount = (key: string, account: StripeBankAccount) => {
		setSelectedAccountId(key)
		onSelectAccount?.(account)
	}

	return (
		<div css={container}>
			<div css={bankAccountList}>
				Add a bank account to pay your order.
				{bankAccounts.map((account) => {
					const key = account.id
					const isNew = account.status === "new"
					const isVerified = account.status === "verified"

					const verificationChip =
						isNew ?
							<span css={verificationChipStyle} className="unverified">
								unverified
							</span>
						:	<span css={verificationChipStyle} className="verified">
								verified
							</span>

					const verificationBadge =
						isNew ?
							<div className="verificationRequired">
								<Icon icon="triangle-exclamation" customColor="#f59e0b" />
								<p>Verification required*</p>
							</div>
						:	null

					const verifyButton =
						isNew ?
							<div css={buttons}>
								<ButtonEcomm
									variant="primary-cta"
									onClick={() => setBankAccountToVerify(account)}
								>
									<span>Verify now</span>
								</ButtonEcomm>
								<Button
									className="removeIcon"
									icon="trash"
									onClick={() => setBankAccountToRemove(account)}
								/>
							</div>
						:	null

					return (
						<>
							<button
								css={accountCheckbox(theme, isNew)}
								key={key}
								type="button"
								className={`account ${
									selectedAccountId === key ? "selected" : ""
								}`}
								onClick={() => setSelectedAccount(key, account)}
							>
								<div>
									<div css={bankTitle}>
										<div>
											<Icon icon="bank" fontSize={1} className="icons" />
											{account.bankName} ...{account.accountNumberLast4}
										</div>
										{verificationChip}
									</div>
									{verificationBadge}
								</div>
								{verifyButton}
								<div css={deleteButtonWhenVerified(isNew)}>
									<Button
										className="removeIcon"
										icon="trash"
										onClick={() => setBankAccountToRemove(account)}
									/>
								</div>
							</button>
							{!isVerified && (
								<p css={depositMessage}>
									*Two small test deposits will be made within 1–2 business days
									for verification.
								</p>
							)}
						</>
					)
				})}
			</div>
			<div css={addButtonContainer}>{addNewBankButton}</div>
			<VerifyBankAccountModal
				isOpen={!!bankAccountToVerify}
				onClose={() => setBankAccountToVerify(null)}
				bankAccount={bankAccountToVerify}
				setSucessfullVerification={() => setVerificationSuccessful(true)}
			/>
			<RemoveMethodModal
				isOpen={!!bankAccountToRemove}
				onClose={() => setBankAccountToRemove(null)}
				bankAccount={bankAccountToRemove}
			/>
			<VerificationSuccessful
				isOpen={verificationSuccessful}
				onClose={() => setVerificationSuccessful(false)}
			/>
		</div>
	)
}

const container = css`
	width: 100%;
`

const accountCheckbox = (theme: Theme, isNew: boolean) => css`
	width: 100%;
	margin-top: 1rem;
	padding: 1rem;
	background-color: #f9fafb;
	border: 1px solid #e5e7eb;
	border-radius: 0.5rem;

	display: flex;
	flex-direction: row;
	justify-content: space-between;
	cursor: pointer;
	transition:
		border 0.2s ease,
		background 0.2s ease;

	&:focus-visible {
		outline: 2px solid #0b75e1;
		outline-offset: 2px;
	}

	&.selected {
		border-color: #0b75e1;
		box-shadow: 0 0 0 2px #0b75e1;
	}

	${theme.breakpoints.down("md")} {
		flex-direction: ${isNew ? "column" : "row"};
		align-items: flex-start;
	}

	div.verificationRequired {
		margin-top: 0.5rem;
		display: flex;
		align-items: center;

		p {
			color: #f59e0b;
			font-size: 0.875rem;
			font-weight: 400;
			margin-left: 0.5rem;
		}
	}
`

const bankAccountList = css`
	margin-left: 3rem;

	.icons {
		margin-right: 0.5rem;
	}

	svg {
		width: 1rem;
		height: 1rem;
	}
`

const depositMessage = css`
	font-style: italic;
	color: #64748b;
	font-size: 12px;
	margin-top: 0.5rem;
	align-self: center;
`

const buttons = (theme: Theme) => css`
	display: flex;
	justify-content: space-between;
	align-items: center;

	${theme.breakpoints.down("md")} {
		margin-top: 0.75rem;
		width: 100%;
	}

	.removeIcon {
		margin-left: 1rem;

		${theme.breakpoints.down("xs")} {
			margin-left: 0;
		}
	}
`
const bankTitle = (theme: Theme) => css`
	display: flex;
	flex-direction: row;
	align-items: center;
	text-align: left;

	${theme.breakpoints.down("md")} {
		flex-direction: column;
		align-items: flex-start;
	}
`

const verificationChipStyle = (theme: Theme) => css`
	font-size: 0.75rem;
	font-weight: 400;
	border-radius: 0.75rem;
	padding: 0 0.4rem;
	margin-left: 0.5rem;

	${theme.breakpoints.down("md")} {
		margin-top: 0.5rem;
		margin-left: 0;
	}

	&.unverified {
		color: #92400e;
		background: #fef3c7;
		margin-left: 1rem;
	}

	&.verified {
		color: #fff;
		background: #10a85f;
	}
`

const addButtonContainer = css`
	display: flex;
	justify-content: flex-end;
	margin-top: 1rem;
`

const deleteButtonWhenVerified = (isNew: boolean) => css`
	display: ${isNew ? "none" : "inline"};
`
